'use client';

interface SpinnerProps {
  /** Diameter in pixels. Default 32. Use ~16 for inline buttons, 48 for full-page loaders. */
  size?: number;
  /** Optional label rendered next to the spinner — also serves as the aria-label. */
  label?: string;
  /** Tailwind border-color class for the active arc. Default: tactical gold. */
  color?: string;
  className?: string;
}

/**
 * Centralized loading spinner. Replaces ad-hoc `animate-spin rounded-full ...`
 * markup scattered across pages. Pass a `label` for screen readers and a
 * visible caption.
 */
export default function Spinner({
  size = 32,
  label,
  color = 'border-operator-gold',
  className = '',
}: SpinnerProps) {
  return (
    <div className={`inline-flex items-center gap-3 ${className}`} role="status">
      <span
        className={`inline-block animate-spin rounded-full border-2 border-transparent border-t-current ${color}`}
        style={{ width: size, height: size }}
        aria-hidden
      />
      {label && <span className="text-sm text-gray-400">{label}</span>}
      <span className="sr-only">{label || 'Loading'}</span>
    </div>
  );
}

/**
 * Skeleton block for content placeholders. Use widths/heights via className.
 *
 * @example
 *   <SkeletonBlock className="h-6 w-32 mb-2" />
 */
export function SkeletonBlock({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-operator-slate/60 ${className}`} aria-hidden />;
}
